import React from 'react';
import FiscalStatusBadge from './FiscalStatusBadge';

type Dashboard = {
  readiness?: { status?: string; completed?: number; total?: number };
  environment?: string;
  electronicBillingEnabled?: boolean;
  certificate?: {
    validityStatus?: string;
    validUntil?: string;
    daysRemaining?: number | null;
    certificateName?: string;
  } | null;
  defaultEcfType?: string;
  nextEncf?: string | null;
  lastElectronicDocument?: {
    encf?: string;
    dgiiStatus?: string;
    createdAt?: string;
  } | null;
  lastSyncAt?: string | null;
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export const FiscalDashboardHeader: React.FC<{ data?: Dashboard | null; loading?: boolean }> = ({
  data,
  loading,
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: 'Estado DGII',
      value: data?.electronicBillingEnabled ? 'Habilitado' : 'Pendiente',
      badge: data?.readiness?.status,
    },
    {
      title: 'Ambiente',
      value: data?.environment === 'PRODUCTION'
        ? 'Producción'
        : data?.environment === 'TEST'
          ? 'TesteCF'
          : 'Certificación',
      badge: data?.environment,
    },
    {
      title: 'Certificado',
      value: data?.certificate?.certificateName || 'No configurado',
      badge: data?.certificate?.validityStatus || 'missing',
    },
    {
      title: 'Vencimiento',
      value: formatDate(data?.certificate?.validUntil),
      hint:
        data?.certificate?.daysRemaining != null
          ? `${data.certificate.daysRemaining} días`
          : undefined,
    },
    {
      title: data?.defaultEcfType || 'e-CF',
      value: data?.nextEncf || '—',
      hint: 'Próximo e-NCF',
    },
    {
      title: 'Último e-CF',
      value: data?.lastElectronicDocument?.encf || 'Ninguno',
      hint: data?.lastElectronicDocument?.dgiiStatus,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <FiscalStatusBadge status={data?.readiness?.status} />
        <span className="text-sm text-slate-500">
          {data?.readiness?.completed ?? 0}/{data?.readiness?.total ?? 0} configuraciones
        </span>
        {data?.lastSyncAt && (
          <span className="text-xs text-slate-400">
            Última sync: {formatDate(data.lastSyncAt)}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div
            key={c.title}
            className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {c.title}
            </p>
            <p className="mt-1 truncate text-sm font-semibold text-slate-900" title={c.value}>
              {c.value}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1">
              {c.badge && <FiscalStatusBadge status={c.badge} />}
              {c.hint && <span className="text-xs text-slate-500">{c.hint}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FiscalDashboardHeader;
